import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, Loader2, AlertCircle, ExternalLink, CheckCircle2, Circle, Target } from "lucide-react";
import { renderRich } from "@/lib/renderRich";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HORIZ_LABELS = { "30d": "Primeros 30 días", "90d": "90 días", "180d": "180 días" };
const HORIZ_ORDER = ["30d", "90d", "180d"];

/**
 * Vista pública read-only de la checklist compartida.
 * URL: /consulting/shared/:token/checklist
 * Reutiliza el `share_token` del informe (uno por test).
 */
export default function ConsultingSharedChecklist() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_BASE}/consulting/public/reports/${token}/checklist`)
      .then((r) => mounted && setState({ loading: false, error: null, data: r.data }))
      .catch((e) => mounted && setState({ loading: false, error: e?.response?.data?.detail || "Enlace no válido", data: null }));
    return () => { mounted = false; };
  }, [token]);

  const grouped = useMemo(() => {
    const items = state.data?.items || [];
    const g = { "30d": [], "90d": [], "180d": [] };
    for (const it of items) {
      if (!g[it.horizonte]) g[it.horizonte] = [];
      g[it.horizonte].push(it);
    }
    return g;
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F6E56]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Enlace no disponible</h1>
          <p className="text-slate-600 mb-6">{state.error}</p>
          <a href="/" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "#0F6E56" }}>
            Ir al inicio <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const { company_name, nickname, items = [], progress } = state.data;
  const pdfUrl = `${API_BASE}/consulting/public/reports/${token}/checklist/pdf`;
  const total = items.length;
  const done = items.filter((it) => it.done).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 md:px-6" style={{ background: "#fafafa" }} data-testid="consulting-shared-checklist">
      <div className="max-w-4xl mx-auto">
        {/* Cabecera */}
        <div className="mb-8">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "#0F6E56" }}>
            Plan de acción · OptimizaRRHH
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
            {nickname || company_name}
          </h1>
          {nickname && company_name && nickname !== company_name && (
            <p className="text-sm text-slate-500 mt-1">{company_name}</p>
          )}
          <p className="text-xs text-slate-400 mt-2">Checklist ejecutivo · 30 · 90 · 180 días</p>
        </div>

        {/* Barra premium con progreso + CTA PDF */}
        <div
          className="mb-8 rounded-2xl px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 20px 45px -25px rgba(11,61,46,0.55)",
          }}
        >
          <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#E5C874" }}>
              Vista compartida · Solo lectura
            </div>
            <div className="text-sm text-white/90 mt-1">
              Progreso: <strong style={{ color: "#E5C874" }}>{done}/{total}</strong> acciones completadas · <strong style={{ color: "#E5C874" }}>{pct}%</strong>
            </div>
            <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 100%)" }} />
            </div>
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap flex-shrink-0 w-full sm:w-auto transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
              color: "#0b3d2e",
              boxShadow: "0 8px 22px -8px rgba(201,168,76,0.55)",
            }}
            data-testid="shared-checklist-pdf-btn"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} /> Descargar PDF
          </a>
        </div>

        {/* Grupos por horizonte */}
        {HORIZ_ORDER.map((h) => {
          const list = grouped[h] || [];
          if (list.length === 0) return null;
          const gDone = list.filter((it) => it.done).length;
          return (
            <section
              key={h}
              className="mb-6 rounded-2xl bg-white border border-slate-200 p-6 md:p-7 relative overflow-hidden"
              style={{ boxShadow: "0 4px 16px -8px rgba(11,61,46,0.08)" }}
              data-testid={`shared-checklist-group-${h}`}
            >
              <span
                aria-hidden
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ background: "linear-gradient(180deg, #0b3d2e 0%, #0F6E56 100%)" }}
              />
              <div className="pl-2 mb-5 flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                >
                  <Target className="w-4 h-4 text-white" strokeWidth={2.25} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Horizonte
                  </div>
                  <div className="text-sm font-bold text-slate-900 font-display">{HORIZ_LABELS[h] || h}</div>
                </div>
                <div className="text-xs font-semibold text-slate-500 whitespace-nowrap">
                  {gDone}/{list.length}
                </div>
              </div>
              <ul className="space-y-3 pl-2" data-testid={`shared-checklist-list-${h}`}>
                {list.map((it, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 rounded-xl p-3 transition-colors"
                    style={{ background: it.done ? "rgba(15,110,86,0.06)" : "#fafafa" }}
                  >
                    {it.done ? (
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#0F6E56" }} strokeWidth={2.25} />
                    ) : (
                      <Circle className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-300" strokeWidth={2} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold leading-snug ${it.done ? "text-slate-500 line-through" : "text-slate-900"}`}>
                        {renderRich(it.accion || it.title || it.action) || "Acción sin título"}
                      </div>
                      {(it.indicador || it.description) && (
                        <div className={`text-xs mt-1 leading-relaxed ${it.done ? "text-slate-400" : "text-slate-600"}`}>
                          {it.done && "✓ "}<span className="text-slate-400 mr-1">Indicador:</span>{renderRich(it.indicador || it.description)}
                        </div>
                      )}
                      {(it.area || it.owner || it.impact) && (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          {it.area && (
                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#fef3c7", color: "#78350f" }}>
                              {it.area}
                            </span>
                          )}
                          {it.owner && (
                            <span className="text-[10px] font-medium text-slate-500 px-2 py-0.5 rounded-full bg-slate-100">
                              {it.owner}
                            </span>
                          )}
                          {it.impact && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(15,110,86,0.08)", color: "#0F6E56" }}>
                              Impacto {it.impact}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {items.length === 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500 text-sm">
            No hay acciones definidas todavía para este plan.
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-10">
          Plan de acción construido con el marco de consultoría OptimizaRRHH · <a href="/" className="hover:underline text-[#0F6E56]">talenthealthcheck.com</a>
        </p>
      </div>
    </div>
  );
}
